import React from "react";
import { isLight, shade } from "../../utils/color";
import type { MockupId, MockupView } from "../../types/configurator";
interface MockupShapeProps {
  mockup: MockupId;
  view: MockupView;
  hex: string;
}
export function MockupShape({
  mockup,
  view,
  hex
}: MockupShapeProps) {
  const light = isLight(hex);
  const line = light ? shade(hex, -0.24) : shade(hex, 0.28);
  const deep = light ? shade(hex, -0.1) : shade(hex, 0.1);
  const body = {
    fill: hex,
    stroke: line,
    strokeWidth: 2,
    strokeLinejoin: "round" as const
  };
  const detail = {
    fill: "none",
    stroke: line,
    strokeWidth: 1.5,
    strokeLinecap: "round" as const
  };
  const back = view === "back";
  function render() {
    switch (mockup) {
      case "tshirt":
        return <>
            <path {...body} d={`M150 58 L104 72 L44 122 L78 182 L114 162 L114 352 Q200 362 286 352 L286 162 L322 182 L356 122 L296 72 L250 58 ${back ? "Q200 76 150 58" : "Q238 94 200 94 Q162 94 150 58"} Z`} data-id="element-40" data-mp="2im8li321doem" />
            <path d={back ? "M152 60 Q200 78 248 60" : "M152 62 Q164 92 200 92 Q236 92 248 62"} fill="none" stroke={deep} strokeWidth={7} data-id="element-41" data-mp="3t7m48l6gjo5y" />
            <path {...detail} d="M104 74 Q118 112 114 162 M296 74 Q282 112 286 162 M66 170 L100 150 M334 170 L300 150" data-id="element-42" data-mp="3kdzdj9h3xb3m" />
          </>;
      case "hoodie":
        return <>
            <path {...body} d="M140 72 L96 88 L50 200 L40 330 L82 336 L98 222 L112 196 L112 352 Q200 362 288 352 L288 196 L302 222 L318 336 L360 330 L350 200 L304 88 L260 72 Z" data-id="element-43" data-mp="3aftc0sz7q6za" />
            <path {...body} fill={deep} d={back ? "M140 72 Q146 18 200 16 Q254 18 260 72 Q240 116 200 118 Q160 116 140 72 Z" : "M140 72 Q146 18 200 16 Q254 18 260 72 Q240 124 200 124 Q160 124 140 72 Z"} data-id="element-44" data-mp="2wi4rjm9251ur" />
            {!back && <>
                <path d="M162 74 Q200 112 238 74 Q232 40 200 38 Q168 40 162 74 Z" fill={shade(hex, light ? -0.3 : -0.4)} data-id="element-45" data-mp="1dvepc325qnec" />
                <path {...detail} d="M184 118 L182 168 M216 118 L218 168" strokeWidth={3} data-id="element-46" data-mp="1imd6t5yvtrj6" />
                <path {...detail} d="M136 280 L264 280 L276 342 L124 342 Z" data-id="element-47" data-mp="14v8ekoxug1iv" />
              </>}
            <path {...detail} d="M98 222 L82 336 M302 222 L318 336 M112 340 L288 340" data-id="element-48" data-mp="19t063huktlmj" />
          </>;
      case "cap":
        return <>
            <path {...body} d="M88 236 Q86 108 200 98 Q314 108 312 236 Z" data-id="element-49" data-mp="1xhjedxdmlyem" />
            {back ? <path d="M164 236 Q200 190 236 236 Z" fill="#F4F4F2" stroke={line} strokeWidth={2} data-id="element-50" data-mp="21imrwd87bico" /> : <path {...body} fill={deep} d="M66 240 Q200 204 334 240 Q336 278 200 268 Q64 278 66 240 Z" data-id="element-51" data-mp="1sk11jvwhmz1c" />}
            <path {...detail} d="M200 100 L200 232 M200 100 Q146 140 136 234 M200 100 Q254 140 264 234" data-id="element-52" data-mp="c5rovz1lyyqc" />
            <circle cx={200} cy={100} r={7} fill={deep} stroke={line} strokeWidth={1.5} data-id="element-53" data-mp="h65nkqhbkwbp" />
          </>;
      case "tote":
        return <>
            <path d="M148 152 Q148 46 200 46 Q252 46 252 152" fill="none" stroke={line} strokeWidth={16} strokeLinecap="round" data-id="element-54" data-mp="4mif44p6wy0r" />
            <path d="M148 152 Q148 46 200 46 Q252 46 252 152" fill="none" stroke={deep} strokeWidth={12} strokeLinecap="round" data-id="element-55" data-mp="imjiprwuajjx" />
            <path {...body} d="M98 150 L302 150 L314 362 L86 362 Z" data-id="element-56" data-mp="dnq0jm6ly4df" />
            <path {...detail} d="M100 164 L300 164" strokeDasharray="4 5" data-id="element-57" data-mp="3m28hkdej0u96" />
          </>;
      case "backpack":
        return <>
            <path d="M178 72 Q200 36 222 72" fill="none" stroke={line} strokeWidth={8} strokeLinecap="round" data-id="element-58" data-mp="3hzlibrfj071y" />
            <path {...body} d="M110 112 Q110 70 200 70 Q290 70 290 112 L300 340 Q300 362 278 362 L122 362 Q100 362 100 340 Z" data-id="element-59" data-mp="3r235q96x35qj" />
            <path {...body} fill={deep} d="M110 112 Q110 70 200 70 Q290 70 290 112 L292 162 Q200 178 108 162 Z" data-id="element-60" data-mp="33bqq0c5fk4pe" />
            <path {...body} fill={deep} d="M130 234 L270 234 L274 330 Q274 346 258 346 L142 346 Q126 346 126 330 Z" data-id="element-61" data-mp="2yce6a09uvdr1" />
            <path {...detail} d="M140 248 L260 248" strokeWidth={2.5} data-id="element-62" data-mp="3c1pl5eo93mw4" />
          </>;
      case "mug":
        return <g transform={back ? "translate(400 0) scale(-1 1)" : undefined} data-id="element-63" data-mp="3gwwg9y3gfj9f">
            <path d="M270 160 Q340 160 340 230 Q340 300 270 300" fill="none" stroke={line} strokeWidth={26} data-id="element-64" data-mp="3lx11u2j7z46i" />
            <path d="M270 160 Q340 160 340 230 Q340 300 270 300" fill="none" stroke={hex} strokeWidth={20} data-id="element-65" data-mp="3qr59ko0b7g8n" />
            <path {...body} d="M110 112 L270 112 L270 332 Q270 352 250 352 L130 352 Q110 352 110 332 Z" data-id="element-66" data-mp="3ut067f7ap5g3" />
            <ellipse cx={190} cy={112} rx={80} ry={12} fill={deep} stroke={line} strokeWidth={2} data-id="element-67" data-mp="8jfsmrgf62k7" />
          </g>;
      case "bottle":
        return <>
            <rect x={174} y={36} width={52} height={44} rx={6} fill={shade(hex, light ? -0.45 : 0.2)} stroke={line} strokeWidth={2} data-id="element-68" data-mp="dgfzh4l3f9cd" />
            <path {...body} d="M180 80 L220 80 L222 110 Q270 130 270 172 L270 350 Q270 366 254 366 L146 366 Q130 366 130 350 L130 172 Q130 130 178 110 Z" data-id="element-69" data-mp="2f9lxs78ny4yh" />
            <path d="M144 180 L144 344" stroke="rgba(255,255,255,0.35)" strokeWidth={6} strokeLinecap="round" data-id="element-70" data-mp="wg2am307vq4g" />
          </>;
      case "notebook":
        return <>
            <path d="M310 66 L318 72 L318 354 L310 348 Z" fill="#EFEBE2" stroke={line} strokeWidth={1.5} data-id="element-71" data-mp="nmz0o4os5vek" />
            <rect {...body} x={98} y={60} width={212} height={290} rx={8} data-id="element-72" data-mp="ip7apg7p4jd0" />
            <rect x={98} y={60} width={24} height={290} rx={6} fill={deep} data-id="element-73" data-mp="1fclmh02ens4p" />
            {!back && <rect x={282} y={60} width={8} height={290} fill={shade(hex, light ? -0.35 : -0.5)} data-id="element-74" data-mp="1bbs2ghs32xh3" />}
          </>;
      case "pen":
        return <>
            <path {...body} d="M44 176 L300 176 L366 200 L300 224 L44 224 Q30 200 44 176 Z" data-id="element-75" data-mp="11dubvnmr4olb" />
            <path d="M300 176 L366 200 L300 224 Z" fill={shade(hex, light ? -0.2 : 0.2)} stroke={line} strokeWidth={2} data-id="element-76" data-mp="3r3672iyky6r1" />
            <rect x={60} y={156} width={120} height={18} rx={4} fill={deep} stroke={line} strokeWidth={1.5} data-id="element-77" data-mp="3m3jri2va4ef2" />
            <path d="M54 190 L290 190" stroke="rgba(255,255,255,0.3)" strokeWidth={4} strokeLinecap="round" data-id="element-78" data-mp="3nl0bl6ok9u0k" />
          </>;
      case "box":
        return <>
            <rect {...body} x={70} y={136} width={260} height={200} rx={4} data-id="element-79" data-mp="3el3h2ofhq0xl" />
            <rect {...body} fill={deep} x={60} y={110} width={280} height={56} rx={4} data-id="element-80" data-mp="3emws0fx4z7be" />
          </>;
      case "card":
        return <>
            <rect {...body} x={84} y={122} width={232} height={156} rx={24} data-id="element-81" data-mp="34t7ilc9hs8su" />
            {!back && <circle cx={110} cy={148} r={8} fill="#F4F4F2" stroke={line} strokeWidth={1.5} data-id="element-82" data-mp="39m90lt4zwogn" />}
          </>;
      case "round":
        return back ? <>
            <circle {...body} cx={200} cy={200} r={150} data-id="element-83" data-mp="2zv5weigrrtd5" />
            <circle {...detail} cx={200} cy={200} r={112} data-id="element-84" data-mp="2ztl6j65egylm" />
          </> : <>
            <circle {...body} cx={200} cy={200} r={150} data-id="element-85" data-mp="2lxzgqwrceas5" />
            <circle cx={200} cy={200} r={126} fill="#FAFAF8" stroke={line} strokeWidth={2} data-id="element-86" data-mp="2qty4qj8rdqs1" />
            {Array.from({
            length: 12
          }).map((_, i) => {
            const a = i * Math.PI / 6;
            return <line key={i} x1={200 + Math.sin(a) * 108} y1={200 - Math.cos(a) * 108} x2={200 + Math.sin(a) * 118} y2={200 - Math.cos(a) * 118} stroke="#0A0A0A" strokeWidth={i % 3 === 0 ? 4 : 2} data-id="element-87" data-mp="22bnzc86bht6m" data-mpi={i} />;
          })}
            <line x1={200} y1={200} x2={200} y2={124} stroke="#0A0A0A" strokeWidth={5} strokeLinecap="round" data-id="element-88" data-mp="2l9qnyluydo3w" />
            <line x1={200} y1={200} x2={252} y2={186} stroke="#0A0A0A" strokeWidth={4} strokeLinecap="round" data-id="element-89" data-mp="2zwjx19dvpxnn" />
            <circle cx={200} cy={200} r={6} fill="#C8102E" data-id="element-90" data-mp="2uzaca71fqa65" />
          </>;
      case "flat":
        return <>
            <rect {...body} x={70} y={68} width={260} height={272} rx={6} data-id="element-91" data-mp="38w7348hjzrn1" />
            <rect x={70} y={296} width={260} height={12} fill={deep} data-id="element-92" data-mp="34ysscce85bsx" />
            <path {...detail} d={Array.from({
            length: 22
          }, (_, i) => `M${76 + i * 12} 340 L${76 + i * 12} 354`).join(" ")} data-id="element-93" data-mp="f5gm5qnq6bx4" />
          </>;
      default:
        return null;
    }
  }
  return <svg viewBox="0 0 400 400" className="h-full w-full" aria-hidden="true" data-id="element-94" data-mp="p60pcyut1qhz">
      {render()}
    </svg>;
}